// MetricsDashboard.js v3.0 - Real Production, Billing & User KPIs
import React, { useState, useEffect } from 'react';
import {
  BarChart3, Activity, Shield, Users, Truck,
  MapPin, Clock, FileText,
  DollarSign, Target, RefreshCw,
  ArrowLeft, Loader, Shovel
} from 'lucide-react';
import { colors } from '../config/constants';
import { fetchWithRedirect } from '../utils/fetchHelpers';
import { loadDashboardStats } from '../services/mapService';

// API URLs
const PORTAL_URL = 'https://script.google.com/macros/s/AKfycbyUHklFqQCDIFzHKVq488fYtAIW1lChNnWV2FWHnvGEr7Eq0oREhDE5CueoBJ6k-xhKOg/exec';
const GATEWAY_URL = 'https://script.google.com/macros/s/AKfycbwv_5HDf9EhFPi0HhW0zqah5TzR1G3o4Hqo8ytdnq-G2xMuIl9_CbHPVcWCU2T2pgvK/exec';
const GATEWAY_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';
const ONBOARDING_SHEET_ID = '1VciM5TqHC5neB7JzpcFkX0qyoyzjBvIS0fKkOXQqnrc';

const MetricsDashboard = ({ darkMode, setCurrentPage }) => {
  const accentPrimary = darkMode ? '#667eea' : '#00b4d8';
  const accentSecondary = darkMode ? '#ff6b35' : '#28a745';
  const bgColor = darkMode ? colors.dark : '#f8fafc';
  const cardBg = darkMode ? colors.darkLight : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalUsers: 0, employees: 0, contractors: 0, admins: 0,
    onboardingSubmissions: 0, pendingOnboarding: 0, activeThisWeek: 0
  });
  const [dashStats, setDashStats] = useState(null);
  const [showVersion, setShowVersion] = useState(false);

  useEffect(() => { fetchMetrics(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      // Fetch users + onboarding + dashboard stats in parallel
      const [usersText, onboardingText, stats] = await Promise.all([
        fetchWithRedirect(PORTAL_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'listUsers' })
        }),
        fetchWithRedirect(GATEWAY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            secret: GATEWAY_SECRET,
            action: 'sheetsRead',
            params: { spreadsheetId: ONBOARDING_SHEET_ID, range: 'A1:Z200' }
          })
        }),
        loadDashboardStats(),
      ]);

      const usersResult = JSON.parse(usersText);
      const onboardingResult = JSON.parse(onboardingText);

      if (usersResult.success) {
        const users = usersResult.users || [];
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        setMetrics(prev => ({
          ...prev,
          totalUsers: users.length,
          employees: users.filter(u => u.role === 'employee').length,
          contractors: users.filter(u => u.role === 'contractor').length,
          admins: users.filter(u => u.role === 'admin').length,
          activeThisWeek: users.filter(u => u.lastLogin && new Date(u.lastLogin) > oneWeekAgo).length
        }));
      }

      if (onboardingResult.success && onboardingResult.data?.data) {
        const rows = onboardingResult.data.data;
        const submissions = rows.slice(1).filter(r => r[0]);
        const pending = submissions.filter(r => r[14] !== 'Approved' && r[14] !== 'Rejected');
        setMetrics(prev => ({
          ...prev,
          onboardingSubmissions: submissions.length,
          pendingOnboarding: pending.length
        }));
      }

      if (stats) setDashStats(stats);
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
    }
    setLoading(false);
  };

  // Helpers
  const phasePct = (phase) => {
    if (!dashStats || !dashStats.segments.total) return 0;
    const p = dashStats.segments[phase];
    return Math.round(((p.complete + p.qcApproved) / dashStats.segments.total) * 100);
  };

  const fmtMoney = (n) => `$${Math.round(n).toLocaleString()}`;

  const StatCard = ({ label, value, icon: Icon, color, subtext }) => (
    <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={22} color={color} />
        </div>
      </div>
      <div style={{ fontSize: '2rem', fontWeight: '700', color: textColor }}>{loading ? '...' : value}</div>
      <div style={{ fontSize: '0.9rem', color: mutedColor }}>{label}</div>
      {subtext && <div style={{ fontSize: '0.75rem', color: mutedColor, marginTop: '4px' }}>{subtext}</div>}
    </div>
  );

  const ProgressBar = ({ label, pct, color }) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ color: textColor, fontWeight: '500' }}>{label}</span>
        <span style={{ color: textColor, fontWeight: '600' }}>{pct}%</span>
      </div>
      <div style={{ height: '10px', backgroundColor: borderColor, borderRadius: '5px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: '5px', transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, padding: '32px' }} onClick={(e) => { if (e.detail === 3) setShowVersion(!showVersion); }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
          <button onClick={() => setCurrentPage('admin-dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: 'transparent', border: `1px solid ${borderColor}`, borderRadius: '8px', color: textColor, cursor: 'pointer' }}>
            <ArrowLeft size={18} /> Back
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: textColor }}>Metrics Dashboard</h1>
            <p style={{ color: mutedColor }}>Operations, production & financial overview</p>
          </div>
          <button onClick={fetchMetrics} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: accentPrimary, color: '#fff', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? <Loader size={18} /> : <RefreshCw size={18} />}
            Refresh
          </button>
        </div>

        {/* Users Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          <StatCard label="Total Users" value={metrics.totalUsers} icon={Users} color={accentPrimary} />
          <StatCard label="Active This Week" value={metrics.activeThisWeek} icon={Activity} color={accentSecondary} subtext="Logged in" />
          <StatCard label="Employees" value={metrics.employees} icon={Users} color="#10b981" />
          <StatCard label="Contractors" value={metrics.contractors} icon={Truck} color="#3b82f6" />
          <StatCard label="Admins" value={metrics.admins} icon={Shield} color="#8b5cf6" />
        </div>

        {/* Production KPIs */}
        {dashStats && (
          <>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor, marginBottom: '16px' }}>Production</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
              <StatCard label="Total Segments" value={dashStats.segments.total.toLocaleString()} icon={MapPin} color={accentPrimary} subtext={`${Math.round(dashStats.segments.totalFootage).toLocaleString()} ft`} />
              <StatCard label="Boring" value={`${phasePct('boring')}%`} icon={Shovel} color="#10b981" subtext={`${dashStats.segments.boring.complete + dashStats.segments.boring.qcApproved} done`} />
              <StatCard label="Pulling" value={`${phasePct('pulling')}%`} icon={Target} color="#3b82f6" subtext={`${dashStats.segments.pulling.complete + dashStats.segments.pulling.qcApproved} done`} />
              <StatCard label="Splicing" value={`${phasePct('splicing')}%`} icon={BarChart3} color="#8b5cf6" subtext={`${dashStats.segments.splicing.complete + dashStats.segments.splicing.qcApproved} done`} />
              <StatCard label="Active Crews" value={dashStats.crews.activeContractors} icon={Truck} color={accentSecondary} />
              <StatCard label="Unassigned" value={dashStats.crews.unassigned} icon={Clock} color="#ef4444" subtext="Segments" />
            </div>

            {/* Billing KPIs */}
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor, marginBottom: '16px' }}>Billing & Revenue</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
              <StatCard label="Revenue (Billed)" value={fmtMoney(dashStats.billing.totalBilled)} icon={DollarSign} color="#f59e0b" />
              <StatCard label="Collected (Paid)" value={fmtMoney(dashStats.billing.totalPaid)} icon={DollarSign} color={accentSecondary} />
              <StatCard label="Outstanding" value={fmtMoney(dashStats.billing.totalPending)} icon={DollarSign} color="#ef4444" />
              <StatCard label="Total Invoices" value={dashStats.billing.invoiceCount} icon={FileText} color={accentPrimary} subtext={`${dashStats.billing.invoicesPaid} paid / ${dashStats.billing.invoicesPending} pending`} />
            </div>

            {/* Progress Bars */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px', marginBottom: '32px' }}>
              <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor, marginBottom: '20px' }}>Phase Completion</h3>
                <ProgressBar label="Boring" pct={phasePct('boring')} color="#10b981" />
                <ProgressBar label="Pulling" pct={phasePct('pulling')} color="#3b82f6" />
                <ProgressBar label="Splicing" pct={phasePct('splicing')} color="#8b5cf6" />
              </div>

              <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor, marginBottom: '20px' }}>Billing Breakdown</h3>
                {(() => {
                  const total = dashStats.billing.totalBilled || 1;
                  const paidPct = Math.round((dashStats.billing.totalPaid / total) * 100);
                  const pendingPct = 100 - paidPct;
                  return (
                    <>
                      <div style={{ display: 'flex', height: '32px', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                        <div style={{ width: `${paidPct}%`, backgroundColor: accentSecondary, transition: 'width 0.5s ease' }} />
                        <div style={{ width: `${pendingPct}%`, backgroundColor: '#ef4444', transition: 'width 0.5s ease' }} />
                      </div>
                      <div style={{ display: 'flex', gap: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: accentSecondary }} />
                          <span style={{ color: mutedColor, fontSize: '0.9rem' }}>Paid ({paidPct}%)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#ef4444' }} />
                          <span style={{ color: mutedColor, fontSize: '0.9rem' }}>Pending ({pendingPct}%)</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </>
        )}

        {/* User Distribution + Onboarding (kept from original) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>
          <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor, marginBottom: '16px' }}>User Distribution</h3>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
              <div style={{ display: 'flex', gap: '32px' }}>
                {[
                  { label: 'Employees', value: metrics.employees, color: '#10b981' },
                  { label: 'Contractors', value: metrics.contractors, color: '#3b82f6' },
                  { label: 'Admins', value: metrics.admins, color: '#8b5cf6' },
                ].map(item => (
                  <div key={item.label} style={{ textAlign: 'center' }}>
                    <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.5rem', fontWeight: '700' }}>
                      {item.value}
                    </div>
                    <div style={{ marginTop: '8px', color: textColor }}>{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor, marginBottom: '16px' }}>Onboarding Pipeline</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: mutedColor }}>Total Submissions</span>
                  <span style={{ color: textColor, fontWeight: '500' }}>{metrics.onboardingSubmissions}</span>
                </div>
                <div style={{ height: '8px', backgroundColor: borderColor, borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: '100%', backgroundColor: accentPrimary, borderRadius: '4px' }} />
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: mutedColor }}>Pending Review</span>
                  <span style={{ color: textColor, fontWeight: '500' }}>{metrics.pendingOnboarding}</span>
                </div>
                <div style={{ height: '8px', backgroundColor: borderColor, borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: metrics.onboardingSubmissions > 0 ? `${(metrics.pendingOnboarding / metrics.onboardingSubmissions) * 100}%` : '0%', backgroundColor: '#f59e0b', borderRadius: '4px' }} />
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: mutedColor }}>Approved</span>
                  <span style={{ color: textColor, fontWeight: '500' }}>{metrics.onboardingSubmissions - metrics.pendingOnboarding}</span>
                </div>
                <div style={{ height: '8px', backgroundColor: borderColor, borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: metrics.onboardingSubmissions > 0 ? `${((metrics.onboardingSubmissions - metrics.pendingOnboarding) / metrics.onboardingSubmissions) * 100}%` : '0%', backgroundColor: accentSecondary, borderRadius: '4px' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Version */}
      {showVersion && (
        <div style={{ position: 'fixed', bottom: '10px', right: '10px', fontSize: '0.7rem', opacity: 0.5, color: textColor, backgroundColor: cardBg, padding: '4px 8px', borderRadius: '4px' }}>
          MetricsDashboard v3.0
        </div>
      )}
    </div>
  );
};

export default MetricsDashboard;
